import { Navigate, useLocation } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useApi';
import type { ReactNode } from 'react';

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const isSystemAdmin = user?.role === 'system_admin';

  // Load tenant data for all authenticated users (system_admins also belong to a tenant)
  const { data: tenant, isLoading: loadingTenant } = useTenant();
  const shouldLoadTenant = isAuthenticated && !location.pathname.startsWith('/admin');

  if (isLoading || (shouldLoadTenant && loadingTenant)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Non-system-admin users: block access to admin routes
  if (!isSystemAdmin && location.pathname.startsWith('/admin')) {
    return <Navigate to="/app" replace />;
  }

  // Redirect to onboarding if tenant is still onboarding
  if (!isSystemAdmin && tenant && tenant.status === 'onboarding' && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // Redirect away from onboarding if tenant is active
  if (!isSystemAdmin && tenant && tenant.status !== 'onboarding' && location.pathname === '/onboarding') {
    return <Navigate to="/app" replace />;
  }

  return <>{children}</>;
}
